import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd, RouterModule } from '@angular/router';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { IconsModule } from '@progress/kendo-angular-icons';
import { filter, takeUntil } from 'rxjs/operators';
import {
  SVGIcon,
  menuIcon,
  chevronLeftIcon,
  chevronDownIcon,
  chevronRightIcon,
  groupIcon,
  gridLayoutIcon,
  lockIcon,
  userIcon,
  gearIcon,
} from '@progress/kendo-svg-icons';
import { BaseComponent } from '../../base/base.component';
import { AccessContextService } from '../../../core/services/access-context.service';

export interface MenuItem {
  title: string;
  route: string;
  icon: string;
  isDisabled: boolean;
  isDivider?: boolean;
  children?: MenuItem[];
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, ButtonsModule, IconsModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent extends BaseComponent implements OnInit {
  private router = inject(Router);
  private accessContext = inject(AccessContextService);

  @Output() sidebarToggle = new EventEmitter<boolean>();
  isOpen = true;
  menuItems: MenuItem[] = [];
  currentRoute = '';
  expandedItems: Set<string> = new Set();

  // Kendo UI SVG Icons
  menuIconSvg: SVGIcon = menuIcon;
  chevronLeftIconSvg: SVGIcon = chevronLeftIcon;
  chevronDownIconSvg: SVGIcon = chevronDownIcon;
  chevronRightIconSvg: SVGIcon = chevronRightIcon;
  groupIconSvg: SVGIcon = groupIcon;
  appsIconSvg: SVGIcon = gridLayoutIcon;
  lockPersonIconSvg: SVGIcon = lockIcon;
  personIconSvg: SVGIcon = userIcon;
  settingsIconSvg: SVGIcon = gearIcon;

  constructor() {
    super();
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.currentRoute = event.url;
          // Auto-expand parent menu if child route is active
          this.autoExpandActiveMenu();
        }
      });
  }

  ngOnInit(): void {
    this.loadMenuItems();
    this.autoExpandActiveMenu();
  }

  toggleSidebar(): void {
    this.isOpen = !this.isOpen;
    this.sidebarToggle.emit(this.isOpen);
  }

  loadMenuItems(): void {
    const items: MenuItem[] = [
      {
        title: 'Tenants',
        route: '/tenants',
        icon: 'group',
        isDisabled: false,
      },
    ];

    if (this.accessContext.canViewTenantAdmins()) {
      items.push({
        title: 'Tenant Members',
        route: '/tenants/tenant-admin',
        icon: 'lockPerson',
        isDisabled: false,
      });
    }

    items.push(this.createDivider('divider-before-users'));
    if (this.accessContext.canViewUsers()) {
      items.push({
        title: 'Users',
        route: '/user',
        icon: 'person',
        isDisabled: false,
      });
    }

    this.menuItems = items.filter((item, index, source) => {
      if (!item.isDivider) {
        return true;
      }

      const previousItem = source[index - 1];
      const nextItem = source[index + 1];
      return Boolean(previousItem && !previousItem.isDivider && nextItem && !nextItem.isDivider);
    });
  }

  private createDivider(route: string): MenuItem {
    return {
      title: '',
      route,
      icon: '',
      isDisabled: true,
      isDivider: true,
    };
  }

  getIcon(iconName: string): SVGIcon {
    const iconMap: { [key: string]: SVGIcon } = {
      group: this.groupIconSvg,
      users: this.groupIconSvg,
      apps: this.appsIconSvg,
      lockPerson: this.lockPersonIconSvg,
      person: this.personIconSvg,
      settings: this.settingsIconSvg,
    };
    return iconMap[iconName] || this.groupIconSvg;
  }

  isActiveRoute(route: string): boolean {
    return this.currentRoute === route;
  }

  hasActiveChild(item: MenuItem): boolean {
    if (!item.children) return false;
    return item.children.some((child) => this.currentRoute === child.route);
  }

  toggleExpand(item: MenuItem): void {
    if (item.children && item.children.length > 0) {
      const key = item.route;
      if (this.expandedItems.has(key)) {
        this.expandedItems.delete(key);
      } else {
        this.expandedItems.add(key);
      }
    }
  }

  isExpanded(item: MenuItem): boolean {
    return this.expandedItems.has(item.route);
  }

  autoExpandActiveMenu(): void {
    this.menuItems.forEach((item) => {
      if (item.children && this.hasActiveChild(item)) {
        this.expandedItems.add(item.route);
      }
    });
  }

  navigate(item: MenuItem, event?: Event): void {
    if (item.isDisabled) {
      event?.preventDefault();
      event?.stopPropagation();
      return;
    }

    if (item.children && item.children.length > 0) {
      event?.preventDefault();
      event?.stopPropagation();
      this.toggleExpand(item);
      return;
    }
  }
}
